/* eslint-disable react/jsx-props-no-spreading */
import { bool, func, object, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Image from 'yoda-core-components/lib/components/Image';
import PointCross from 'yoda-core-components/lib/components/PointCross/PointCross';
import * as styles from './ProductImage.css';

const cx = classNames.bind(styles);

const ProductImage = ({
    altImageUrl,
    cardTheme,
    imageSrc,
    imageUrl,
    isAppointmentCards,
    lazyLoadImages,
    onLoad,
    onProductClick,
    openInNewTab,
    pointCrossOffsetProps,
    productUrl,
    swatchImageSrc,
    title,
    sponsoredProduct,
    showVerticalProductImage,
}) => {
    let url = imageSrc || imageUrl || '';
    const [defaultImageUrl, setDefaultImageUrl] = useState(url);
    const [displayImageUrl, setDisplayImageUrl] = useState(url);
    const isCartridgeTheme = cardTheme === 'cartridge';

    const setImageUrl = () => {
        const isS7Image = /scene7.com(.+)/g.test(url);

        if (!imageSrc && isS7Image && cardTheme !== 'gallery') {
            if (imageUrl?.indexOf('hei=') > 0 && imageUrl?.indexOf('wid=') > 0) {
                url = imageUrl.replace(/hei=(.*?)&/, 'hei=170&').replace(/wid=(.*?)&/, 'wid=170&');
            } else
                url = `${imageUrl}${
                    imageUrl?.indexOf('?') > 0 ? '&' : '?'
                }hei=170&wid=170&op_sharpen=1`;
        }

        setDefaultImageUrl(url);
        setDisplayImageUrl(url);
    };

    useEffect(() => {
        setImageUrl();
    }, [url]);

    const config = {
        anchor: {
            className: dt(['h-full', 'w-full']),
            href: productUrl,
            onClick: (event) => {
                onProductClick(event, 'image');
            },
            rel: openInNewTab ? 'noopener noreferrer' : null,
            target: openInNewTab ? '_blank' : null,
            ...(sponsoredProduct && !openInNewTab && { rel: 'sponsored' }),
        },
        image: {
            alt: title,
            className: isCartridgeTheme
                ? `${dt(['max-h-full', 'w-full', 'max-w-full', 'relative', 'sm:pb-[100%]'])} ${cx(
                      'imageProdBlock'
                  )}`
                : `${dt([
                      'relative',
                      'bg-cover',
                      'h-full',
                      'max-w-full',
                      'w-full',
                      'sm:min-h-[130px]',
                      'sm:pb-[100%]',
                  ])} ${cx('imageProdBlock', {
                      verticalImageProdBlock: showVerticalProductImage,
                  })}`,
            isLazy: !isCartridgeTheme,
            onMouseLeave: () => {
                altImageUrl && setDisplayImageUrl(swatchImageSrc || defaultImageUrl);
            },
            onMouseOver: () => {
                altImageUrl && setDisplayImageUrl(altImageUrl);
            },
            onLoad,
            src: displayImageUrl,
        },
        pointCross: {
            ...pointCrossOffsetProps,
            wrapperClass: dt([
                'h-full',
                'relative',
                'w-full',
                isCartridgeTheme ? 'p-0' : '',
                isAppointmentCards ? 'pb-0' : '',
            ]),
        },
    };

    const renderImage = (
        <div
            className={dt([
                'h-full',
                'relative',
                'w-full',
                isCartridgeTheme ? 'p-0' : '',
                isAppointmentCards ? 'pb-0' : '',
            ])}
        >
            {lazyLoadImages ? (
                <PointCross {...config?.pointCross}>
                    <Image {...config?.image} />
                </PointCross>
            ) : (
                <Image {...config?.image} />
            )}
        </div>
    );

    return productUrl ? <a {...config?.anchor}>{renderImage}</a> : renderImage;
};

/* istanbul ignore next */
ProductImage.defaultProps = {
    altImageUrl: '',
    cardTheme: 'gallery',
    imageSrc: '',
    imageUrl: '',
    isAppointmentCards: false,
    lazyLoadImages: false,
    onLoad: () => {},
    onProductClick: () => {},
    openInNewTab: false,
    pointCrossOffsetProps: { offset: 500 },
    productUrl: '',
    swatchImageSrc: '',
    title: '',
    sponsoredProduct: false,
    showVerticalProductImage: false,
};

ProductImage.propTypes = {
    altImageUrl: string,
    cardTheme: string,
    imageSrc: string,
    imageUrl: string,
    isAppointmentCards: bool,
    lazyLoadImages: bool,
    onLoad: func,
    onProductClick: func,
    openInNewTab: bool,
    pointCrossOffsetProps: object,
    productUrl: string,
    swatchImageSrc: string,
    title: string,
    sponsoredProduct: bool,
    showVerticalProductImage: bool,
};

export default React.memo(ProductImage);
